import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import AudioMixer from './AudioMixer';
import { YouCard, SubScreenHeader, softButtonSx } from './YouPrimitives';
import {
  loadCustomTuning,
  saveGuitarTuning,
  DEFAULT_4_STRING_TUNING,
  DEFAULT_5_STRING_TUNING,
  DEFAULT_6_STRING_TUNING,
  DEFAULT_7_STRING_TUNING,
  DEFAULT_8_STRING_TUNING,
  DEFAULT_9_STRING_TUNING
} from '../utils/guitarNeckUtils';
import { ALL_NOTES } from '../utils/musicTheory';
import { track } from '../utils/analytics';
import { isTipsEnabled, setTipsEnabled } from '../utils/tipsPreference';
import { useUserSetting } from '../data/hooks/useUserSetting';
// ─── LAB POINT 5, TEMPORAIRE : à retirer à la clôture (voir RETRAIT dans utils/perfVariant.js) ───
import { perfLabEnabled, noLessonAnimations, bypassReverb, setPerfVariant } from '../utils/perfVariant';
// ─── fin LAB POINT 5 ───

// Version number. Le numero entre parentheses est le build en cours de diffusion.
// A tenir a jour avec versionCode dans android/app/build.gradle et
// CURRENT_PROJECT_VERSION dans le projet Xcode. Ils ne sont pas forcement
// egaux : au 01/09/2026, Android est au 103 et iOS au 102 (deja envoye chez
// Apple, donc fige). Ce libelle suit le build de la plateforme diffusee.
//
// Cette constante a menti pendant deux builds : elle annoncait encore 101 sur
// un appareil qui portait le 102, ce qui rend tout test de version impossible
// a lire. La verite est dans `adb shell dumpsys package com.christophenovalet.upscale`
// cote Android, et dans l'Info.plist de l'archive cote iOS.
export const appVersion = "1.0.3 (107)";

// Available presets per string count
const TUNING_PRESETS = {
  4: [
    { name: 'Standard E', notes: DEFAULT_4_STRING_TUNING },
    { name: 'Drop D', notes: ['D', 'A', 'D', 'G'] }
  ],
  5: [
    { name: 'Standard B', notes: DEFAULT_5_STRING_TUNING },
    { name: 'Drop A', notes: ['A', 'E', 'A', 'D', 'G'] }
  ],
  6: [
    { name: 'Standard E', notes: DEFAULT_6_STRING_TUNING },
    { name: 'Drop D', notes: ['D', 'A', 'D', 'G', 'B', 'E'] },
    { name: 'Drop C', notes: ['C', 'G', 'C', 'F', 'A', 'D'] },
    { name: 'Drop B', notes: ['B', 'F#', 'B', 'E', 'G#', 'C#'] },
    { name: 'Drop A', notes: ['A', 'E', 'A', 'D', 'F#', 'B'] },
    { name: 'C Standard', notes: ['C', 'F', 'A#', 'D#', 'G', 'C'] },
    { name: 'B Standard', notes: ['B', 'E', 'A', 'D', 'F#', 'B'] },
    { name: 'A Standard', notes: ['A', 'D', 'G', 'C', 'E', 'A'] },
    { name: 'Double Drop D', notes: ['D', 'A', 'D', 'G', 'B', 'D'] },
    { name: 'Open C', notes: ['C', 'G', 'C', 'G', 'C', 'E'] },
    { name: 'Eb Standard', notes: ['D#', 'G#', 'C#', 'F#', 'A#', 'D#'] },
    { name: 'DADGAD', notes: ['D', 'A', 'D', 'G', 'A', 'D'] },
    { name: 'Open G', notes: ['D', 'G', 'D', 'G', 'B', 'D'] },
    { name: 'All Fourths', notes: ['E', 'A', 'D', 'G', 'C', 'F'] },
  ],
  7: [
    { name: 'Standard B', notes: DEFAULT_7_STRING_TUNING },
    { name: 'Drop A', notes: ['A', 'E', 'A', 'D', 'G', 'B', 'E'] },
    { name: 'Drop G', notes: ['G', 'D', 'G', 'C', 'F', 'A', 'D'] },
    { name: 'Eb Modern', notes: ['A#', 'D#', 'G#', 'C#', 'F#', 'A#', 'D#'] },
    { name: 'Jazz Standard A', notes: ['A', 'D', 'G', 'C', 'E', 'A', 'D'] },
    { name: 'All Fourths', notes: ['B', 'E', 'A', 'D', 'G', 'C', 'F'] },
  ],
  8: [
    { name: 'Standard F#', notes: DEFAULT_8_STRING_TUNING },
    { name: 'Drop E', notes: ['E', 'B', 'E', 'A', 'D', 'G', 'B', 'E'] },
    { name: 'Drop Eb', notes: ['D#', 'A#', 'D#', 'G#', 'C#', 'F#', 'A#', 'D#'] },
    { name: 'F# std w/ low E', notes: ['E', 'A', 'D', 'G', 'C', 'F', 'A', 'D'] },
    { name: 'All Fourths', notes: ['F#', 'B', 'E', 'A', 'D', 'G', 'C', 'F'] },
  ],
  9: [
    { name: 'Standard C#', notes: DEFAULT_9_STRING_TUNING },
    { name: 'Drop C', notes: ['C', 'G', 'C', 'F', 'A', 'D', 'G', 'B', 'E'] },
    { name: 'F# Low', notes: ['F#', 'B', 'E', 'A', 'D', 'G', 'C', 'F', 'A'] },
    { name: 'Djent G#', notes: ['G#', 'C#', 'F#', 'B', 'E', 'A', 'D', 'G', 'B'] },
    { name: 'AEA DGCFAD', notes: ['A', 'E', 'A', 'D', 'G', 'C', 'F', 'A', 'D'] },
    { name: 'All Fourths', notes: ['C#', 'F#', 'B', 'E', 'A', 'D', 'G', 'C', 'F'] },
  ]
};

const findPresetName = (notes) => {
  const presets = TUNING_PRESETS[notes.length] || [];
  const match = presets.find(p => p.notes.length === notes.length && p.notes.every((n, i) => n === notes[i]));
  return match ? match.name : 'Custom';
};

const cardTitleSx = { fontSize: 17, fontWeight: 600, color: '#fff', mb: '12px' };

export default function Settings({ onBack, wide = false }) {
  const [leftHanded, setLeftHanded] = useUserSetting('leftHanded', false);
  const [fretMarkerDots, setFretMarkerDots] = useUserSetting('fretMarkerDots', true);
  const [neckInlays, setNeckInlays] = useUserSetting('neckInlays', false);

  // ---------------------- Custom Tuning State ----------------------
  const initialTuning = loadCustomTuning();
  const [stringCount, setStringCount] = useState(initialTuning.length);
  const [tuning, setTuning] = useState(initialTuning);

  // Preset name tracking ("Standard E", "Drop D", etc.). Derive du tuning
  // sauvegarde : le preset dont les notes correspondent, sinon Custom. Poser le
  // preset standard dans un effet sur stringCount ecrasait le tuning charge des
  // le montage, donc les Settings affichaient toujours Standard E.
  const [presetName, setPresetName] = useState(() => findPresetName(initialTuning));

  const handleStringCountChange = (count) => {
    const defaultPreset = TUNING_PRESETS[count][0];
    setStringCount(count);
    setTuning(defaultPreset.notes);
    setPresetName(defaultPreset.name);
  };

  const handleNoteChange = (idx, value) => {
    const updated = [...tuning];
    updated[idx] = value;
    setTuning(updated);
    setPresetName('Custom');
  };

  const handleSaveTuning = () => {
    saveGuitarTuning(tuning);
  };

  const handleResetTuning = () => {
    const defaultPreset = TUNING_PRESETS[stringCount][0];
    setTuning(defaultPreset.notes);
    setPresetName(defaultPreset.name);
  };

  // Handle preset dropdown change
  const handlePresetChange = (e) => {
    const newName = e.target.value;
    const preset = TUNING_PRESETS[stringCount].find(p => p.name === newName);
    if (preset) {
      setTuning(preset.notes);
      setPresetName(newName);
    }
  };

  const [tipsOn, setTipsOn] = useState(isTipsEnabled);

  const handleTipsToggle = (event) => {
    const next = event.target.checked;
    setTipsOn(next);
    setTipsEnabled(next);
    track('tips_toggled', { enabled: next });
  };

  // Reglage d'essai : on veut savoir s'il sert avant de le sortir du Lab.
  const handleFretMarkerDotsToggle = (event) => {
    const next = event.target.checked;
    setFretMarkerDots(next);
    track('fret_marker_dots_toggled', { enabled: next });
  };

  const handleNeckInlaysToggle = (event) => {
    const next = event.target.checked;
    setNeckInlays(next);
    track('neck_inlays_toggled', { enabled: next });
  };

  // ─── LAB POINT 5, TEMPORAIRE : à retirer à la clôture (voir RETRAIT dans utils/perfVariant.js) ───
  // Aucun état React : setPerfVariant recharge la page, et les deux interrupteurs
  // se relisent alors dans la variante fraîchement posée.
  const handleLessonAnimationsToggle = (event) => {
    setPerfVariant({ animations: event.target.checked, reverb: !bypassReverb() });
  };

  const handlePianoReverbToggle = (event) => {
    setPerfVariant({ animations: !noLessonAnimations(), reverb: event.target.checked });
  };
  // ─── fin LAB POINT 5 ───

  // Rejouer le guide : on efface les deux drapeaux qu'il pose, puis on recharge.
  // Le guide ne s'arme qu'au montage, un rechargement est le chemin le plus
  // court et le seul qui remet aussi l'ecran Play en face de la personne.
  const handleReplayGuide = () => {
    try {
      localStorage.removeItem('onboardingCompleted');
      localStorage.removeItem('firstTimePopupSeen');
    } catch {
      // stockage indisponible : rien a effacer, le guide se rejouera de toute facon
    }
    track('onboarding_replay_requested');
    window.location.reload();
  };

  // Le toggle Tips de docs/getting-started.md vit ici, a cote d'Audio.
  const tipsCard = (
    <YouCard>
      <Typography sx={cardTitleSx}>Tips</Typography>
      <List disablePadding>
        <ListItem
          disableGutters
          secondaryAction={
            <Switch
              edge="end"
              checked={tipsOn}
              onChange={handleTipsToggle}
              inputProps={{ 'aria-label': 'Show tips' }}
            />
          }
        >
          <ListItemText
            primary="Show tips"
            secondary="Small hints when you start out and when a new feature lands. Turn off if you'd rather find things yourself."
          />
        </ListItem>
        <ListItem
          disableGutters
          secondaryAction={
            <Button variant="contained" disableElevation onClick={handleReplayGuide} sx={softButtonSx}>
              Replay
            </Button>
          }
        >
          <ListItemText
            primary="Replay the guide"
            secondary="Start the first-time walkthrough again, from the first chord to your first jam."
          />
        </ListItem>
      </List>
    </YouCard>
  );

  // Morceaux partages entre la liste (telephone) et la grille (tablette, desktop).
  // Sans argument, `size` retombe sur le defaut MUI : le rendu telephone ne bouge pas.
  const renderStringCountSelect = (size) => (
    <FormControl fullWidth size={size}>
      <InputLabel id="string-count-label">Number of Strings</InputLabel>
      <Select
        labelId="string-count-label"
        value={stringCount}
        label="Number of Strings"
        onChange={(e) => handleStringCountChange(e.target.value)}
      >
        <MenuItem value={4}>4</MenuItem>
        <MenuItem value={5}>5</MenuItem>
        <MenuItem value={6}>6</MenuItem>
        <MenuItem value={7}>7</MenuItem>
        <MenuItem value={8}>8</MenuItem>
        <MenuItem value={9}>9</MenuItem>
      </Select>
    </FormControl>
  );

  const renderPresetSelect = (size) => (
    <FormControl fullWidth size={size}>
      <InputLabel id="preset-label">Tuning Preset</InputLabel>
      <Select
        labelId="preset-label"
        value={presetName}
        label="Tuning Preset"
        onChange={handlePresetChange}
      >
        {TUNING_PRESETS[stringCount].map((p) => (
          <MenuItem key={p.name} value={p.name}>{p.name}</MenuItem>
        ))}
        <MenuItem value="Custom" disabled>Custom (manual)</MenuItem>
      </Select>
    </FormControl>
  );

  const renderNoteSelect = (note, idx, size) => (
    <FormControl fullWidth size={size}>
      <InputLabel id={`note-${idx}`}>Note</InputLabel>
      <Select
        labelId={`note-${idx}`}
        value={note}
        label="Note"
        onChange={(e) => handleNoteChange(idx, e.target.value)}
      >
        {ALL_NOTES.map((n) => (
          <MenuItem key={n} value={n}>{n}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderTuningButtons = (size) => (
    <>
      <Button variant="contained" disableElevation size={size} onClick={handleSaveTuning} sx={{ mr: 2, borderRadius: '8px', textTransform: 'none' }}>
        Save Tuning
      </Button>
      <Button variant="contained" disableElevation size={size} onClick={handleResetTuning} sx={softButtonSx}>
        Reset to Default
      </Button>
    </>
  );

  const audioCard = (
    <YouCard>
      <Typography sx={cardTitleSx}>Audio</Typography>

      <AudioMixer embedded />
    </YouCard>
  );

  const fretboardCard = (
    <YouCard>
      <Typography sx={cardTitleSx}>Fretboard</Typography>
      <List disablePadding>
        <ListItem
          disableGutters
          secondaryAction={
            <Switch
              edge="end"
              checked={leftHanded}
              onChange={(event) => setLeftHanded(event.target.checked)}
              inputProps={{ 'aria-label': 'Left-handed mode' }}
            />
          }
        >
          <ListItemText
            primary="Left-handed mode"
            secondary="Flip the fretboard so the nut is on the right."
          />
        </ListItem>
        {/* L'eprouvette annonce un essai : le reglage peut changer de forme ou
            disparaitre, et ce n'est pas la meme promesse que Left-handed. */}
        <ListItem
          disableGutters
          secondaryAction={
            <Switch
              edge="end"
              checked={fretMarkerDots}
              onChange={handleFretMarkerDotsToggle}
              inputProps={{ 'aria-label': 'Fret position dots' }}
            />
          }
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Fret position dots
                <ScienceIcon
                  titleAccess="Lab feature"
                  sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }}
                />
              </Box>
            }
            secondary="The inlays of a real neck, inside the empty circles: one dot on frets 3, 5, 7, 9, two on the 12th. Only when View writes nothing in them."
          />
        </ListItem>
        <ListItem
          disableGutters
          secondaryAction={
            <Switch
              edge="end"
              checked={neckInlays}
              onChange={handleNeckInlaysToggle}
              inputProps={{ 'aria-label': 'Neck inlays' }}
            />
          }
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Neck inlays
                <ScienceIcon
                  titleAccess="Lab feature"
                  sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }}
                />
              </Box>
            }
            secondary="The pearl dots between the strings. Turn them off when the dots sit in the circles, two reminders of the same fret is one too many."
          />
        </ListItem>
        {/* ─── LAB POINT 5, TEMPORAIRE : à retirer à la clôture (voir RETRAIT dans utils/perfVariant.js) ─── */}
        {/* Sans le drapeau de build REACT_APP_PERF_LAB, les deux interrupteurs
            n'existent pas : ils ne servent qu'aux builds de mesure. */}
        {perfLabEnabled() && (<>
        <ListItem
          disableGutters
          secondaryAction={
            <Switch
              edge="end"
              checked={!noLessonAnimations()}
              onChange={handleLessonAnimationsToggle}
              inputProps={{ 'aria-label': 'Lesson animations' }}
            />
          }
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Lesson animations
                <ScienceIcon
                  titleAccess="Lab feature"
                  sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }}
                />
              </Box>
            }
            secondary="Temporary performance test: turns off the fretboard animations during lessons. The app reloads."
          />
        </ListItem>
        <ListItem
          disableGutters
          secondaryAction={
            <Switch
              edge="end"
              checked={!bypassReverb()}
              onChange={handlePianoReverbToggle}
              inputProps={{ 'aria-label': 'Reverb (all instruments)' }}
            />
          }
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Reverb (all instruments)
                <ScienceIcon
                  titleAccess="Lab feature"
                  sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }}
                />
              </Box>
            }
            secondary="Temporary performance test: every instrument in the app plays dry, with no reverb at all. The app reloads."
          />
        </ListItem>
        </>)}
        {/* ─── fin LAB POINT 5 ─── */}
      </List>
    </YouCard>
  );

  // Le tuning ouvre la page : c'est le reglage qu'on vient changer le plus souvent.
  // Deux rendus, la grille desktop tient les cordes cote a cote, la liste les empile.
  const tuningCardWide = (
    <YouCard>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', mb: '12px' }}>
        <Typography sx={{ ...cardTitleSx, mb: 0 }}>Tuning</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {renderTuningButtons('small')}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
        {renderStringCountSelect('small')}
        {renderPresetSelect('small')}
      </Box>

      {/* Une cellule par corde, 68 px minimum (six par rangee sur desktop) : au-dela, les cordes passent
          a la ligne plutot que de tronquer la note. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(68px, 1fr))', gap: '6px', mt: '12px' }}>
        {tuning.map((note, idx) => (
          <Box key={idx}>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', textAlign: 'center', mb: '4px' }}>
              {`String ${idx + 1}`}
            </Typography>
            {renderNoteSelect(note, idx, 'small')}
          </Box>
        ))}
      </Box>
    </YouCard>
  );

  const tuningCardList = (
    <YouCard>
      <Typography sx={cardTitleSx}>Tuning</Typography>

      <List>
        {/* String count selector */}
        <ListItem>
          {renderStringCountSelect()}
        </ListItem>

        {/* Preset dropdown, filtered by string count */}
        <ListItem>
          {renderPresetSelect()}
        </ListItem>

        {/* Per-string note selectors (display from lowest string) */}
        {tuning.map((note, idx) => (
          <ListItem key={idx}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120 }}>{`String ${idx + 1}`}</Typography>
              {renderNoteSelect(note, idx)}
            </Box>
          </ListItem>
        ))}

        <ListItem>
          {renderTuningButtons()}
        </ListItem>
      </List>
    </YouCard>
  );

  if (wide) {
    return (
      <Box className="settings-page" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <SubScreenHeader title="Settings" onBack={onBack} />

        {/* Cote a cote seulement quand chaque carte garde 420 px (desktop) :
            sur iPad les deux cartes s'empilent en pleine largeur. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '16px', alignItems: 'start' }}>
          {tuningCardWide}

          {fretboardCard}

          {audioCard}

          {tipsCard}
        </Box>
      </Box>
    );
  }

  return (
    <Box className="settings-page" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <SubScreenHeader title="Settings" onBack={onBack} />

      {tuningCardList}

      {fretboardCard}

      {audioCard}

      {tipsCard}
    </Box>
  );
}
